import * as React from "react";
import { TextBoxEditor } from "../../../../../../../react-core/Html/TextBoxEditor";
import {
    Alphanumeric,
    AlphanumericWithSeperator,
    AlphanumericWithSpecialCharacters,
    EndWithTwoNumbers,
} from "../../../../../../../react-core/Html/Validation/RegExPatterns";
import {
    CompanyTypeMap,
    CompanyTypeOption,
    FATCABusinessTypeMap,
    FATCABusinessTypeOption,
    LawtoApplyMap,
    LawtoApplyOption,
} from "../../Resource/Contents";
import { SelectEditor } from "../../../../../../../react-core/Html/SelectEditor";
import { RadiosEditor } from "../../../../../../../react-core/Html/RadiosEditor";
import { FATCABusinessType } from "../../../../../../../Wrap/Constants/Enums/Customer/FATCABusinessType";
import { IProcessComponentProps, ProcessComponent } from "../../../../../../Shared/Process/Process/ProcessComponent";
import { CustomerType } from "../../../../../../../Wrap/Constants/Enums/Customer/CustomerType";
import { IAddClientModel } from "../AddClientWizard";
import { CompanyType } from "../../../../../../../Wrap/Constants/Enums/Customer/CompanyType";
import { FATCACountry } from "./FATCACountry";
import { ServiceProposition } from "../../../../../../../Wrap/Constants/Enums/ServiceProposition";

interface ITrustDetailProps extends IProcessComponentProps {
    data: IAddClientModel;
}

interface ITrustDetailState {
    AddAnotherTaxResidency: boolean;
}

export class TrustDetail extends ProcessComponent<ITrustDetailProps, ITrustDetailState> {
    constructor(props: ITrustDetailProps) {
        super(props);
        this.state = {
            AddAnotherTaxResidency: false,
        };
    }

    componentDidMount() {
        super.componentDidMount();
        if (this.props.data?.CustomerType == CustomerType.CorporateTrust) {
            this.onDataChange(
                this.props.onChange,
                {
                    CompanyType: CompanyType.NotSpecified,
                    CustomerType: this.props.data?.CustomerType,
                    IsNewTrust: false,
                },
                this.props.data,
                "Organisation"
            );
        } else {
            this.onDataChange(
                this.props.onChange,
                {
                    CustomerType: this.props.data?.CustomerType,
                    IsNewTrust:
                        this.props.data?.SelectedFirmProposition?.length > 0 &&
                        this.props.data?.SelectedFirmProposition.find((x) => x == ServiceProposition.Fundzone) !=
                            null &&
                        this.props.data?.SelectedFirmProposition.find((x) => x == ServiceProposition.Wrap) == null
                            ? false
                            : true,
                },
                this.props.data,
                "Organisation"
            );
        }
    }

    render() {
        const { data, onChange } = this.props;

        return (
            <>
                <TextBoxEditor
                    cssWrapper="control-size-430"
                    value={data?.Organisation?.Name}
                    label={data.CustomerType == CustomerType.Trust ? "Trust name" : "Corporate name"}
                    regex={{
                        errorMessage: "Only alphanumeric and special characters allowed.",
                        value: AlphanumericWithSpecialCharacters,
                    }}
                    validationRules={[
                        {
                            errorMessage: "Name cannot be spaces only",
                            validator: (v) => {
                                return v.replace(/\s/g, "").length != 0;
                            },
                        },
                    ]}
                    onChange={(value) => this.onDataChange(onChange, { Name: value }, data, "Organisation")}
                    required={"Please enter name"}
                    maxLength={50}
                />

                <RadiosEditor
                    cssWrapper="rg-inline mb-16"
                    label={"New or existing"}
                    tooltip={
                        data.CustomerType == CustomerType.CorporateTrust
                            ? null
                            : "A new trust is a trust that is created as part of the opening of a trust wrap account. An existing trust is a trust which already exists at the time the trust wrap account is opened."
                    }
                    selected={this.props.data?.Organisation?.IsNewTrust}
                    options={[true, false]}
                    getText={(v) => (v ? "New" : "Existing")}
                    onChange={(v) => {
                        this.onDataChange(this.props.onChange, { IsNewTrust: v }, this.props.data, "Organisation");
                    }}
                    disabled={
                        this.props.data?.CustomerType == CustomerType.CorporateTrust ||
                        (this.props.data?.SelectedFirmProposition?.length > 0 &&
                            this.props.data?.SelectedFirmProposition.find((x) => x == ServiceProposition.Fundzone) !=
                                null &&
                            this.props.data?.SelectedFirmProposition.find((x) => x == ServiceProposition.Wrap) == null)
                    }
                />

                <TextBoxEditor
                    cssWrapper="control-size-430"
                    value={data?.Organisation?.LEICode}
                    tooltip={
                        "LEI means Legal Entity Identifier. It is a code unique to a legal entity or structure and required for transaction reporting purposes. Please see the FCA website for detail."
                    }
                    label={"LEI"}
                    validationRules={[
                        {
                            errorMessage: "LEI Code must contain 20 characters or blank.",
                            validator: (v) => (v.length == 20 || v.length == 0 ? true : false),
                        },
                        {
                            errorMessage: "LEI code must contain only numbers or letters.",
                            validator: (v) => Alphanumeric.test(v),
                        },
                        {
                            errorMessage: "Last 2 characters of LEI Code must be numbers.",
                            validator: (v) => v.length == 0 || EndWithTwoNumbers.test(v),
                        },
                    ]}
                    maxLength={20}
                    onChange={(value) => this.onDataChange(onChange, { LEICode: value }, data, "Organisation")}
                />

                <label>Law to apply</label>
                <RadiosEditor
                    cssWrapper="radio-group rg-150 mb-23"
                    selected={data?.Organisation?.LawToApply}
                    options={LawtoApplyOption}
                    getText={(v) => {
                        return LawtoApplyMap[v];
                    }}
                    onChange={(v) =>
                        this.onDataChange(
                            onChange,
                            {
                                LawToApply: v,
                            },
                            data,
                            "Organisation"
                        )
                    }
                    required="Please choose Law to apply"
                />

                {data.CustomerType == CustomerType.CorporateTrust && (
                    <SelectEditor
                        options={CompanyTypeOption}
                        getText={(v) => {
                            return CompanyTypeMap[v];
                        }}
                        cssWrapper="control-size-430"
                        required="Please select"
                        validationRules={[
                            {
                                errorMessage: "Please select",
                                validator: (v) => {
                                    return v != CompanyType.NotSpecified;
                                },
                            },
                        ]}
                        label="Company type"
                        selected={data?.Organisation?.CompanyType}
                        onChange={(value) => this.onDataChange(onChange, { CompanyType: value }, data, "Organisation")}
                    />
                )}

                <SelectEditor
                    options={FATCABusinessTypeOption}
                    getText={(v) => {
                        return FATCABusinessTypeMap[v];
                    }}
                    tooltip={`Under UK legislation we are required to collect certain tax information about the type of business that invests with us and possibly report some of that information to HMRC who in turn may share that information with other tax authorities around the world as appropriate.

                    The Foreign Account Tax Compliance Act (‘’FATCA’’) was introduced by the U.S. government in 2010. The UK adopted the frame work into UK legislation with an effective date of 1 July 2014. FATCA requires us as platform provider to undertake specified due diligence, reporting and oversight obligations to detect and deter U.S. tax evasion.
                    
                    The Common Reporting Standard (‘’CRS’’) was introduced by the OECD and implemented into UK law with an effective date of 1 January 2016. Broadly similar to FATCA, the legislation requires us as platform provider to undertake essentially the same due diligence, reporting and oversight obligations to detect and deter tax evasion.
                    
                    Please note that we are unable to provide tax advice in respect of the business type or questions on tax residency in respect of the business. In case of any additional questions we would refer you to your tax advisor and/or the HMRC guidance available online.`}
                    cssWrapper="control-size-430"
                    required="Please select"
                    tooltipCss={"is-info-icon ml-5 is-wide"}
                    validationRules={[
                        {
                            errorMessage: "Please select",
                            validator: (v) => {
                                return v != FATCABusinessType.NotSpecified;
                            },
                        },
                    ]}
                    label="FATCA/CRS Business Type"
                    selected={data?.Organisation?.FATCABusinessType}
                    onChange={(v) => {
                        this.onDataChange(
                            onChange,
                            { FATCABusinessType: v, IsTaxPurposeOtherThanUk: false },
                            data,
                            "Organisation"
                        );
                    }}
                />
                {(data?.Organisation?.FATCABusinessType == FATCABusinessType.InvestmentBody ||
                    data?.Organisation?.FATCABusinessType == FATCABusinessType.ActiveNFFE) && (
                    <>
                        <label>Is the entity resident for tax purposes anywhere other than the UK?</label>
                        <RadiosEditor
                            cssWrapper="radio-group rg-150 mb-23"
                            selected={data?.Organisation?.IsTaxPurposeOtherThanUk}
                            options={[true, false]}
                            getText={(v) => {
                                return v ? "Yes" : "No";
                            }}
                            onChange={(v) => {
                                this.onDataChange(
                                    onChange,
                                    { IsTaxPurposeOtherThanUk: v, FatcaCountries: v ? [{}] : [] },
                                    data,
                                    "Organisation"
                                );
                            }}
                            required="Please choose"
                        />
                    </>
                )}
                {data?.Organisation?.IsTaxPurposeOtherThanUk && (
                    <>
                        <FATCACountry
                            data={this.props.data}
                            onChange={(v) => {
                                this.onDataChange(onChange, { FatcaCountries: v }, data, "Organisation");
                            }}
                            onRef={(ref) => {
                                this.appendChild(ref);
                            }}
                        />
                    </>
                )}

                {data?.Organisation?.FATCABusinessType === FATCABusinessType.FFI && (
                    <TextBoxEditor
                        cssWrapper="control-size-430"
                        value={data?.Organisation?.GIIN}
                        regex={{
                            errorMessage: "Please enter alphanumeric and should be equal 19 chars",
                            value: AlphanumericWithSeperator,
                        }}
                        label={"GIIN"}
                        maxLength={19}
                        onChange={(value) => this.onDataChange(onChange, { GIIN: value }, data, "Organisation")}
                        required="Please enter"
                    />
                )}
            </>
        );
    }
}
